import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import AxiosInstance from "../../utils/Axios.ts";
import type Parent from "./parentInterface.ts";

const POLL_MS = 10_000;

/**
 * Polls parent sync metadata so all viewers pick up child booking refreshes triggered by others.
 */
export default function useParentSyncPoll(
  pk: string,
  fetchTours: () => Promise<void>,
  setParent: Dispatch<SetStateAction<Parent | null>>
) {
  const pollParentRef = useRef<{ ts: string | null; status: string | null }>({
    ts: null,
    status: null,
  });

  useEffect(() => {
    pollParentRef.current = { ts: null, status: null };

    const poll = async () => {
      try {
        const { data } = await AxiosInstance.get(`/parent/${pk}/`);
        const prev = pollParentRef.current;
        const ts = data.last_child_sync_at ?? null;
        const st = data.sync_status ?? null;
        const syncAdvanced =
          prev.ts !== null &&
          (ts !== prev.ts || st !== prev.status) &&
          st !== "in_progress";

        setParent(data);
        if (syncAdvanced) {
          await fetchTours();
        }
        pollParentRef.current = { ts, status: st };
      } catch {
        /* transient network errors — ignore */
      }
    };

    const interval = setInterval(poll, POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pk, fetchTours, setParent]);
}
